import {
    ContactsOverview,
    NewContacts,
    ContactField,
    ContactPattern,
    Contact,
    ContactHistory,
    ContactUploadPayload,
    ContactsGrowth,
    ContactsPatchPayload,
    ContactUpload,
    LinkContacts,
    ExportLink,
    ExportFileFormats,
    CompressionFormat,
    UnsubscribedOverview,
    ContactStatus,
    UnsubscribedContactsOrderBy,
    ContactInfo,
    ContactReshardStatus,
    AddContactPayload,
} from '@dev/ee-api/ee-api-models';

import { AxiosPromise, AxiosResponse } from 'axios';
import { ExportContactsParams } from '@/store/exports/types';
import { PostWithPatternParams, GetContactsInterface } from '@/store/contacts/types';

import { request } from './request';

export const loadNewContacts = (params: { From: string; To: string }): Promise<NewContacts[]> => {
    const { Contacts } = request();
    return Contacts.contactsNetcontactsGet(params.From, params.To).then((resp): NewContacts[] => resp.data);
};

export const loadContactsOverview = (params: { From: string; To: string }): Promise<ContactsOverview> => {
    const { Contacts } = request();
    return Contacts.contactsOverviewGet(params.From, params.To).then((resp): ContactsOverview => resp.data);
};

export const getContactsPattern = (params: { fileName: string; encodingName?: string }): Promise<ContactPattern> => {
    const { Contacts } = request();
    return Contacts.contactsPatternGet(params.fileName, params.encodingName).then((resp): ContactPattern => resp.data);
};

export const getContacts = (params: GetContactsInterface): AxiosPromise<Contact[]> => {
    const { Contacts } = request();
    return Contacts.contactsGet(
        params.statuses,
        params.search,
        params.rule,
        params.limit,
        params.offset,
        params.orderBy
    ).then((resp): AxiosResponse<Contact[]> => resp);
};

export const postContactsPattern = (params: PostWithPatternParams): Promise<ContactUpload> => {
    const { Contacts } = request();
    return Contacts.contactsPatternPost(
        { Filename: params.FileName, Pattern: params.Pattern, OverwriteCustomFields: params.OverwriteCustomFields },
        params.listName,
        params.createList,
        params.status,
        params.encodingName,
        params.consentTracking
    ).then((resp): ContactUpload => resp.data);
};

export const postContacts = (params: ContactUploadPayload): Promise<ContactUpload> => {
    const timeout = 60000; //60s

    const { Contacts } = request({ timeout });
    return Contacts.contactsPost(params).then((resp): ContactUpload => resp.data);
};

export const getContactFields = (): Promise<ContactField[]> => {
    const { Contacts } = request();
    return Contacts.contactsFieldsGet().then((resp): ContactField[] => resp.data);
};

export const createContactField = (params: ContactField): Promise<ContactField> => {
    const { Contacts } = request();
    return Contacts.contactsFieldsPut(params.Name, params.Type, params.Length).then((resp): ContactField => resp.data);
};

export const deleteContactField = (name: string): AxiosPromise<void> => {
    const { Contacts } = request();
    return Contacts.contactsFieldsByNameDelete(name);
};

export const exportContacts = (params: ExportContactsParams): Promise<ExportLink> => {
    const { Contacts } = request();
    return Contacts.contactsTasksExportGet(
        params.fileFormat,
        params.rule,
        params.emails,
        params.compressionFormat,
        params.fileName
    ).then((resp): ExportLink => resp.data);
};

export const deleteContacts = (params: { rule?: string; emails?: string[] }): AxiosPromise<number> => {
    const { Contacts } = request();
    return Contacts.contactsDelete(params.rule, params.emails);
};

export const loadSingleContact = (email: string): Promise<Contact> => {
    const { Contacts } = request();
    return Contacts.contactsByEmailGet(email).then((resp): Contact => resp.data);
};
export const editSingleContact = (params: {
    email: string;
    firstName: string;
    lastName: string;
    customFields: Record<string, any>;
}): Promise<Contact> => {
    const { Contacts } = request();
    return Contacts.contactsByEmailPut(params.email, {
        FirstName: params.firstName,
        LastName: params.lastName,
        CustomFields: params.customFields,
    }).then((resp): Contact => resp.data);
};

export const getContactHistory = (params: {
    email: string;
    limit: number;
    offset: number;
}): AxiosPromise<ContactHistory[]> => {
    const { Contacts } = request();
    return Contacts.contactsByEmailHistoryGet(params.email, params.limit, params.offset);
};

export const bulkUpdateContacts = (params: ContactsPatchPayload): AxiosPromise<void> => {
    const { Contacts } = request();
    return Contacts.contactsPatch(params);
};

export const getContactsGrowth = (from: string, to: string): AxiosPromise<ContactsGrowth[]> => {
    const { Contacts } = request();
    return Contacts.contactsGrowthGet(from, to).then((resp): AxiosResponse<ContactsGrowth[]> => resp);
};

export type LinkContactsParams = {
    link: string;
    channelName?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    activePage?: number;
    linkContactsLoading?: boolean;
    linkContactsAllLoading?: boolean;
    perPage?: number;
};

export const getLinkContacts = (params: LinkContactsParams): AxiosPromise<LinkContacts> => {
    const { Contacts } = request();

    return Contacts.contactsLinkcontactsGet(
        params.link,
        params.channelName,
        params.from,
        params.to,
        params.limit,
        params.offset
    );
};

export type ExportLinkContactsParams = {
    link: string;
    channelName?: string;
    from?: string;
    to?: string;
    fileFormat?: ExportFileFormats;
    limit?: number;
    offset?: number;
    compressionFormat?: CompressionFormat;
    fileName?: string;
};
export const exportLinkContacts = (params: ExportLinkContactsParams): AxiosPromise<ExportLink> => {
    const { Contacts } = request();

    return Contacts.contactsLinkcontactsExportGet(
        params.link,
        params.channelName,
        params.from,
        params.to,
        params.fileFormat,
        params.limit,
        params.offset,
        params.compressionFormat,
        params.fileName
    );
};

export const getContactsUnsubscribedOverview = (from: string, to: string): AxiosPromise<UnsubscribedOverview> => {
    const { Contacts } = request();

    return Contacts.contactsUnsubscribedOverviewGet(from, to);
};

export type ContactsUnsubscribedParams = {
    statuses: ContactStatus[];
    from?: string;
    to?: string;
    email?: string;
    orderBy?: UnsubscribedContactsOrderBy;
    limit: number;
    offset: number;
};

export const getContactsUnsubscribed = (params: ContactsUnsubscribedParams): AxiosPromise<ContactInfo[]> => {
    const { Contacts } = request();

    return Contacts.contactsUnsubscribedGet(
        params.statuses,
        params.from,
        params.to,
        params.email,
        params.orderBy,
        params.limit,
        params.offset
    );
};

export type ExportContactsUnsubscribedParams = {
    statuses: ContactStatus[];
    from?: string;
    to?: string;
    email?: string;
    orderBy?: UnsubscribedContactsOrderBy;
    fileFormat: ExportFileFormats;
    compressionFormat: CompressionFormat;
    fileName: string;
};

export const exportContactsUnsubscribed = (params: ExportContactsUnsubscribedParams): AxiosPromise<ExportLink> => {
    const { Contacts } = request();

    return Contacts.contactsUnsubscribedExportGet(
        params.statuses,
        params.from,
        params.to,
        params.email,
        params.orderBy,
        params.fileFormat,
        params.compressionFormat,
        params.fileName
    );
};

export type ExportSuppressionsParams = {
    fileFormat?: ExportFileFormats;
    rule?: string;
    emails?: string[];
    compressionFormat?: CompressionFormat;
    fileName?: string;
    selectedAll?: boolean;
};

export const countActiveContacts = (countContactsIn: string[], allContacts?: boolean): Promise<number> => {
    const { Contacts } = request();

    return Contacts.contactsCountactivePost({ CountContactsIn: countContactsIn, AllContacts: allContacts }).then(
        (resp): number => resp.data
    );
};

export const reshardContacts = (): AxiosPromise<void> => {
    const { Contacts } = request();
    return Contacts.contactsReshardPost();
};

export const checkReshardStatus = (): AxiosPromise<ContactReshardStatus> => {
    const { Contacts } = request();
    return Contacts.contactsReshardGet();
};

export const addContactToListPost = (params: AddContactPayload): AxiosPromise<string> => {
    const { Contacts } = request();
    return Contacts.contactsAddcontacttolistPost(params);
};
